import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RESEARCH_DRAWER_MAX_HEIGHT } from './constants';
import { getResearchParagraphs } from './researchContent';

const ResearchAccordion = ({
  id,
  title,
  icon: Icon,
  sourceCount,
  companyName,
  expanded,
  onToggle,
}) => {
  const paragraphs = useMemo(
    () => getResearchParagraphs(id, companyName),
    [id, companyName],
  );

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={onToggle}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: '#fff',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 8px 0' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 48,
          px: 1.5,
          '& .MuiAccordionSummary-content': {
            my: 2,
            width: '100%',
            mr: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          },
        }}
      >
        <Box display='flex' alignItems='center' gap={2} minWidth={0}>
          <Icon sx={{ fontSize: 24, color: 'primary.main', flexShrink: 0 }} />
          <Box>
            <Typography variant='body2' fontWeight={600} noWrap>
              {title}
            </Typography>
            {sourceCount != null && (
              <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
              </Typography>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        <Box
          sx={{
            maxHeight: RESEARCH_DRAWER_MAX_HEIGHT,
            overflowY: 'auto',
            pr: 1,
            // Keep scrollbar visible/usable when multiple drawers are open
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: 3,
            },
          }}
        >
          {paragraphs.map((paragraph, index) => (
            <Typography
              key={`${id}-${index}`}
              variant='body2'
              color='text.secondary'
              sx={{ lineHeight: 1.7, mb: index === paragraphs.length - 1 ? 0 : 1.5 }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ResearchAccordion;
